import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Avatar, Box, ButtonBase, Chip, IconButton, ListItemIcon, Menu, MenuItem, Tooltip, Typography } from '@mui/material';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import AddPhotoAlternateOutlinedIcon from '@mui/icons-material/AddPhotoAlternateOutlined';
import HideImageOutlinedIcon from '@mui/icons-material/HideImageOutlined';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DriveFileMoveOutlinedIcon from '@mui/icons-material/DriveFileMoveOutlined';
import VerticalAlignTopIcon from '@mui/icons-material/VerticalAlignTop';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import RestaurantIcon from '@mui/icons-material/Restaurant';

import { offShelfLabel } from 'util/offShelf';
import { KITCHEN_FLAGS, splitPrintCategories } from '../menuEditorPayloads';
import { visibilityNotes } from './labels';

const formatPrice = (symbol, price) => {
  const value = Number(price);
  return Number.isFinite(value) ? `${symbol}${value.toFixed(2)}` : '—';
};

const priceText = (dish, symbol) => {
  const sizes = (dish.sizes || []).filter((size) => size && size.price !== undefined && size.price !== '');
  if (sizes.length === 0) {
    return formatPrice(symbol, dish.price);
  }
  return `${sizes.length} size${sizes.length > 1 ? 's' : ''} · from ${formatPrice(
    symbol,
    Math.min(...sizes.map((size) => Number(size.price)))
  )}`;
};

const DishRow = ({
  dish,
  image,
  offShelf,
  currencySymbol,
  showIds,
  groupCount,
  drag,
  canMoveToTop,
  onOpen,
  onUploadImage,
  onRemoveImage,
  onDuplicate,
  onMove,
  onMoveToTop,
  onDelete,
}) => {
  const [anchor, setAnchor] = useState(null);
  const close = () => setAnchor(null);
  const run = (action) => () => {
    close();
    action();
  };

  const notes = visibilityNotes(dish);
  const flags = KITCHEN_FLAGS.filter((flag) => dish[flag.key]).map((flag) => flag.label);
  const meta = [
    dish.gstFree ? 'GST free' : null,
    ...flags,
    ...splitPrintCategories(dish.printCategory).map((name) => `Prints: ${name}`),
    dish.cookingCategory ? `Cooking: ${dish.cookingCategory}` : null,
    groupCount > 0 ? `${groupCount} choice group${groupCount > 1 ? 's' : ''}` : null,
  ].filter(Boolean);

  return (
    <Box
      ref={drag && drag.rowRef}
      data-testid="dish-row"
      sx={{
        display: 'flex',
        alignItems: 'stretch',
        borderBottom: '1px solid',
        borderColor: 'divider',
        opacity: drag && drag.isDragging ? 0.4 : 1,
        bgcolor: 'background.paper',
        '&:hover': { bgcolor: 'action.hover' },
        '&:hover .dish-handle': { opacity: 1 },
      }}
    >
      {drag ? (
        <Box
          ref={drag.handleRef}
          className="dish-handle"
          aria-label={`Drag to reorder ${dish.name}`}
          sx={{ display: 'flex', alignItems: 'center', px: 0.5, color: 'text.disabled', cursor: 'grab', opacity: { xs: 1, md: 0.35 } }}
        >
          <DragIndicatorIcon fontSize="small" />
        </Box>
      ) : (
        <Box sx={{ width: 12 }} />
      )}
      <ButtonBase
        onClick={onOpen}
        aria-label={`Edit ${dish.name}`}
        sx={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-start', textAlign: 'left', gap: 2, py: 1.5, pr: 1 }}
      >
        <Avatar
          variant="rounded"
          src={image || undefined}
          alt=""
          sx={{ width: 56, height: 56, bgcolor: 'grey.100', color: 'grey.400', borderRadius: 1.5 }}
        >
          <RestaurantIcon />
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
            {dish.colorHex && (
              <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: dish.colorHex, flexShrink: 0, alignSelf: 'center' }} />
            )}
            {dish.displayNumber && (
              <Typography variant="body2" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                {dish.displayNumber}
              </Typography>
            )}
            <Typography variant="body1" sx={{ fontWeight: 600, flex: 1, minWidth: 0, overflowWrap: 'anywhere' }}>
              {dish.name || 'Untitled dish'}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 500, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
              {priceText(dish, currencySymbol)}
            </Typography>
          </Box>
          {(dish.altName || dish.description) && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 0.25, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
            >
              {[dish.altName, dish.description].filter(Boolean).join(' · ')}
            </Typography>
          )}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.75 }}>
            {offShelf && offShelf.live && (
              <Chip size="small" color="error" variant="outlined" label={offShelfLabel(offShelf.live)} />
            )}
            {notes.map((note) => (
              <Chip key={note} size="small" color="warning" variant="outlined" label={note} />
            ))}
            {(dish.tags || []).map((tag) => (
              <Chip key={tag} size="small" label={tag} sx={{ bgcolor: 'grey.100' }} />
            ))}
            {(dish.promotionTags || []).map((tag) => (
              <Chip key={`promo-${tag}`} size="small" color="secondary" variant="outlined" label={tag} />
            ))}
            {showIds && <Chip size="small" variant="outlined" label={`ID ${dish.id}`} sx={{ fontFamily: 'monospace' }} />}
          </Box>
          {meta.length > 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              {meta.join(' · ')}
            </Typography>
          )}
        </Box>
      </ButtonBase>
      <Box sx={{ display: 'flex', alignItems: 'center', pr: 1 }}>
        <Tooltip title="More actions">
          <IconButton aria-label={`More actions for ${dish.name}`} onClick={(event) => setAnchor(event.currentTarget)}>
            <MoreVertIcon />
          </IconButton>
        </Tooltip>
        <Menu anchorEl={anchor} open={!!anchor} onClose={close} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
          <MenuItem onClick={run(onOpen)}>
            <ListItemIcon><EditOutlinedIcon fontSize="small" /></ListItemIcon>
            Edit
          </MenuItem>
          <MenuItem onClick={run(onUploadImage)}>
            <ListItemIcon><AddPhotoAlternateOutlinedIcon fontSize="small" /></ListItemIcon>
            {image ? 'Replace photo' : 'Add photo'}
          </MenuItem>
          {image && (
            <MenuItem onClick={run(onRemoveImage)}>
              <ListItemIcon><HideImageOutlinedIcon fontSize="small" /></ListItemIcon>
              Remove photo
            </MenuItem>
          )}
          <MenuItem onClick={run(onDuplicate)}>
            <ListItemIcon><ContentCopyIcon fontSize="small" /></ListItemIcon>
            Duplicate
          </MenuItem>
          <MenuItem onClick={run(onMove)}>
            <ListItemIcon><DriveFileMoveOutlinedIcon fontSize="small" /></ListItemIcon>
            Move to another category
          </MenuItem>
          {canMoveToTop && (
            <MenuItem onClick={run(onMoveToTop)}>
              <ListItemIcon><VerticalAlignTopIcon fontSize="small" /></ListItemIcon>
              Move to top
            </MenuItem>
          )}
          <MenuItem onClick={run(onDelete)} sx={{ color: 'error.main' }}>
            <ListItemIcon><DeleteOutlineIcon fontSize="small" color="error" /></ListItemIcon>
            Delete
          </MenuItem>
        </Menu>
      </Box>
    </Box>
  );
};

DishRow.propTypes = {
  dish: PropTypes.object.isRequired,
  image: PropTypes.string,
  offShelf: PropTypes.object,
  currencySymbol: PropTypes.string.isRequired,
  showIds: PropTypes.bool,
  groupCount: PropTypes.number,
  drag: PropTypes.object,
  canMoveToTop: PropTypes.bool,
  onOpen: PropTypes.func.isRequired,
  onUploadImage: PropTypes.func.isRequired,
  onRemoveImage: PropTypes.func.isRequired,
  onDuplicate: PropTypes.func.isRequired,
  onMove: PropTypes.func.isRequired,
  onMoveToTop: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
};

export default DishRow;
